import { computed, getCurrentInstance, onUnmounted, ref, Ref, watch } from "@vue/composition-api";
import { MUTATION_TYPES } from "@/store/const";
import appStorage from "@/lib/appStorage";
import emitter from "@/lib/emitter";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getMediaDuration } from "@/lib/time";
import { EVENTS } from "@/const";
import { AudioFileData, GlobalAudioPlayerAction, GlobalAudioPlayerActions, GlobalAudioPlayerFileState } from "../types";

export const STORAGE_KEY = "g-player_volume";

export default function useAudioPlayer(file: Ref<AudioFileData | null | undefined>, onEndCb?: () => void) {
  const vm = getCurrentInstance();
  const audio = ref(new Audio());
  const isPlay = ref(false);
  const duration = ref(0);
  const currentTime = ref(0);
  const volume = ref(appStorage.getNumber(STORAGE_KEY, 100));
  const muted = ref(false);

  const remainingTime = computed(() => Math.floor(duration.value - currentTime.value));

  const formattedRemainingTime = computed(() => getMediaDuration(remainingTime.value * 1000));
  const percentageRemainingTime = computed(() => {
    return duration.value > 0 ? ((currentTime.value / duration.value) * 100).toFixed(2) : 0;
  });

  const state = computed<GlobalAudioPlayerFileState>(() => ({
    uuid: file.value?.uuid ?? "",
    isPlay: isPlay.value,
    currentTime: currentTime.value,
    formattedRemainingTime: formattedRemainingTime.value,
  }));

  const emitUpdate = (uuid = file.value?.uuid) => {
    if (!uuid) return;
    vm?.$store.commit(MUTATION_TYPES.SET_GLOBAL_PLAYER, state.value);
  };

  const play = () => {
    audio.value.play().then();
  };

  const pause = () => {
    audio.value.pause();
  };

  const toggleAudio = () => {
    if (!audio.value) return;
    isPlay.value ? pause() : play();
  };

  const setTime = (seconds: number) => {
    if (!audio.value) return;
    audio.value.currentTime = seconds;
  };

  const setPlay = () => {
    isPlay.value = true;
  };

  const setPause = () => {
    isPlay.value = false;
  };

  const onEnded = () => {
    onEndCb && onEndCb();
  };

  const onTimeUpdate = () => {
    currentTime.value = Math.floor(audio.value.currentTime);
  };

  const onDurationChange = () => {
    duration.value = audio.value.duration;
  };

  const onAction = (data?: GlobalAudioPlayerAction) => {
    if (!data?.name) return;
    switch (data.name) {
      case GlobalAudioPlayerActions.TOGGLE_AUDIO:
        data.uuid === file.value?.uuid && toggleAudio();
        break;
      case GlobalAudioPlayerActions.SET_TIME:
        data.uuid === file.value?.uuid && setTime(data.time);
        break;
      case GlobalAudioPlayerActions.PAUSE:
        pause();
        break;
    }
  };

  watch(file, (file, oldFile) => {
    audio.value.pause();
    isPlay.value = false;
    duration.value = 0;
    currentTime.value = 0;
    oldFile && emitUpdate(oldFile?.uuid);
    if (file) {
      audio.value.src = MediaFilesController.getFileUrl(file.urlPart);
      audio.value.play().then();
    }
  });

  watch(
    volume,
    volume => {
      audio.value.volume = volume / 100;
      muted.value = !volume;
      appStorage.setNumber(STORAGE_KEY, volume);
    },
    { immediate: true }
  );

  watch(muted, muted => {
    audio.value.muted = muted;
    if (!muted && !volume.value) volume.value = 50;
  });

  watch(state, () => emitUpdate());

  audio.value.addEventListener("play", setPlay);
  audio.value.addEventListener("pause", setPause);
  audio.value.addEventListener("ended", onEnded);
  audio.value.addEventListener("durationchange", onDurationChange);
  audio.value.addEventListener("timeupdate", onTimeUpdate);

  emitter.on<GlobalAudioPlayerAction>(EVENTS.AUDIO_PLAYER_ACTION, onAction);

  onUnmounted(() => {
    audio.value.removeEventListener("play", setPlay);
    audio.value.removeEventListener("pause", setPause);
    audio.value.removeEventListener("ended", onEnded);
    audio.value.removeEventListener("durationchange", onDurationChange);
    audio.value.removeEventListener("timeupdate", onTimeUpdate);

    emitter.off<GlobalAudioPlayerAction>(EVENTS.AUDIO_PLAYER_ACTION, onAction);
  });

  return {
    isPlay,
    duration,
    currentTime,
    remainingTime,
    percentageRemainingTime,
    formattedRemainingTime,
    volume,
    muted,
    play,
    pause,
    toggleAudio,
    setTime,
  };
}
